import React, { useState, useEffect } from "react";
import axios from "axios";
import { Wand2, Plus, Trash2, Save, Loader2, ArrowUp, ArrowDown, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AssignmentRules() {
  const token = localStorage.getItem("auth_token");
  const headers = { Authorization: `Bearer ${token}` };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rules, setRules] = useState([]);
  const [boats, setBoats] = useState([]);
  const [cats, setCats] = useState([]);
  const [boatCategory, setBoatCategory] = useState({});
  const [overrides, setOverrides] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [showRedistribute, setShowRedistribute] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/assignment-rules`, { headers });
      setRules(data.config.rules || []);
      setBoatCategory(data.config.boat_category || {});
      setOverrides(data.config.category_overrides || []);
      setShowRedistribute(!!data.config.show_redistribute);
      setBoats(data.boats || []);
      setCats(data.categories || []);
    } catch (e) { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggleRedistribute = async (val) => {
    setShowRedistribute(val);
    try { await axios.put(`${API}/api/assignment-rules`, { show_redistribute: val }, { headers }); }
    catch (e) { setShowRedistribute(!val); alert("Kaydedilemedi: " + (e?.response?.data?.detail || "")); }
  };

  const catName = (slug) => cats.find(c => c.slug === slug)?.name || slug;

  const update = (i, field, val) => {
    setRules(r => r.map((row, idx) => idx === i ? { ...row, [field]: val } : row));
    setDirty(true);
  };
  const addRule = () => {
    setRules(r => [...r, { category: cats[0]?.slug || "", contains: "", time: "", target: boats[0] || "" }]);
    setDirty(true);
  };
  const removeRule = (i) => { setRules(r => r.filter((_, idx) => idx !== i)); setDirty(true); };
  const move = (i, dir) => {
    setRules(r => {
      const n = [...r]; const j = i + dir;
      if (j < 0 || j >= n.length) return n;
      [n[i], n[j]] = [n[j], n[i]]; return n;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/assignment-rules`, { rules, boat_category: boatCategory, category_overrides: overrides }, { headers });
      setDirty(false);
    } catch (e) { alert("Kaydedilemedi: " + (e?.response?.data?.detail || "")); }
    setSaving(false);
  };
  const updateOv = (i, field, val) => { setOverrides(o => o.map((r, idx) => idx === i ? { ...r, [field]: val } : r)); setDirty(true); };
  const addOv = () => { setOverrides(o => [...o, { from: cats[0]?.slug || "", to: cats[0]?.slug || "", time: ">=20:30" }]); setDirty(true); };
  const removeOv = (i) => { setOverrides(o => o.filter((_, idx) => idx !== i)); setDirty(true); };

  const [applying, setApplying] = useState(false);
  const applyFuture = async (force) => {
    if (dirty) { alert("Önce kuralları KAYDET, sonra uygula."); return; }
    const msg = force
      ? "Kuralları TÜM ileri tarihlere uygula? (Bugün HARİÇ)\n\nDİKKAT: 'Sıfırla' modu — elle atanmış (kilitli) satırlar da kurala göre yeniden dizilir."
      : "Kuralları TÜM ileri tarihlere uygula? (Bugün HARİÇ)\n\nElle atanmış (kilitli) satırlar korunur.";
    if (!window.confirm(msg)) return;
    setApplying(true);
    try {
      const { data } = await axios.post(`${API}/api/sheet/auto-assign`,
        { date: new Date().toISOString().slice(0, 10), redistribute: true, all_future: true, skip_today: true, force: !!force },
        { headers });
      alert(data?.message || "Uygulandı");
    } catch (e) { alert("Uygulanamadı: " + (e?.response?.data?.detail || "")); }
    setApplying(false);
  };

  if (loading) return <div className="p-8 text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor...</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="assignment-rules-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Wand2 className="w-5 h-5 text-emerald-400" />
          <div>
            <h1 className="text-lg font-bold text-white">Dağıtım Kuralları</h1>
            <p className="text-xs text-slate-500">Rezervasyonlar bu kurallara göre otomatik olarak doğru tekne bölmesine yerleştirilir. Sırayla değerlendirilir — <b>ilk eşleşen kazanır</b>.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => applyFuture(false)} disabled={applying || dirty} title="Kuralları tüm ileri tarihlere uygula (bugün hariç, kilitliler korunur)"
            className="flex items-center gap-1.5 px-3 py-2 bg-sky-600 hover:bg-sky-500 disabled:opacity-40 text-white text-sm font-semibold rounded-lg" data-testid="rules-apply-btn">
            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            İleri Tarihlere Uygula
          </button>
          <button onClick={() => applyFuture(true)} disabled={applying || dirty} title="Elle atananlar dahil TÜM satırları sıfırlayıp kurala göre yeniden diz (bugün hariç)"
            className="flex items-center gap-1.5 px-3 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-white text-sm font-semibold rounded-lg" data-testid="rules-apply-force-btn">
            <Wand2 className="w-4 h-4" /> Sıfırla &amp; Uygula
          </button>
          <button onClick={save} disabled={saving || !dirty}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-sm font-semibold rounded-lg" data-testid="rules-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {dirty ? "Kaydet" : "Kaydedildi"}
          </button>
        </div>
      </div>

      <div className="mb-3 p-3 rounded-lg bg-slate-800/60 border border-slate-700 flex items-start justify-between gap-4" data-testid="redistribute-setting">
        <div>
          <div className="text-sm font-semibold text-white">Canlı Sayfa'da "Yeniden Dağıt" butonları</div>
          <p className="text-xs text-slate-400 mt-0.5 max-w-2xl">Kapalıyken Canlı Sayfa'daki <b>"Yeniden Dağıt"</b> ve <b>"Hard Yeniden Dağıt"</b> butonları görünmez; böylece elle yapılan tekne atamaları yanlışlıkla sıfırlanmaz. Açarsanız butonlar yeniden görünür.</p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer shrink-0 select-none" data-testid="toggle-redistribute">
          <span className={`text-xs font-medium ${showRedistribute ? "text-emerald-400" : "text-slate-500"}`}>{showRedistribute ? "Açık" : "Kapalı"}</span>
          <button type="button" role="switch" aria-checked={showRedistribute} onClick={() => toggleRedistribute(!showRedistribute)}
            className={`relative w-11 h-6 rounded-full transition-colors ${showRedistribute ? "bg-emerald-600" : "bg-slate-600"}`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${showRedistribute ? "translate-x-5" : ""}`} />
          </button>
        </label>
      </div>

      <div className="mb-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-xs text-blue-200 flex gap-2">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <b>İçeren:</b> ürün adında aranan kelime (aksan önemsiz, boş = herhangi). Örn: <code>cibali</code>, <code>kabatas</code>, <code>dinner</code>.<br />
          <b>Saat:</b> boş = her saat · <code>&lt;=16:00</code> = 16:00 ve öncesi · <code>&gt;16:00</code> = 16:00 sonrası · <code>&gt;=20:30</code> gibi. Operatörler: <code>&lt;</code> <code>&lt;=</code> <code>&gt;</code> <code>&gt;=</code>. Örn: Cibali <code>&lt;=16:00</code> → Daytime, Cibali <code>&gt;16:00</code> → Sunset.<br />
          <b>Hedef:</b> gidilecek tekne bölmesi. Eşleşme yoksa rezervasyon "Atanmamış"ta kalır.
        </div>
      </div>

      <div className="rounded-lg border border-slate-700 overflow-hidden" data-testid="rules-table">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-800 text-[11px] uppercase text-slate-400">
            <tr>
              <th className="px-2 py-2 w-10">#</th>
              <th className="px-2 py-2">Kategori</th>
              <th className="px-2 py-2">İçeren kelime</th>
              <th className="px-2 py-2 w-32">Saat</th>
              <th className="px-2 py-2">Hedef Tekne</th>
              <th className="px-2 py-2 w-28"></th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r, i) => (
              <tr key={i} className="border-t border-slate-800 hover:bg-slate-800/40" data-testid={`rule-row-${i}`}>
                <td className="px-2 py-1.5 text-slate-500 text-xs">{i + 1}</td>
                <td className="px-2 py-1.5">
                  <select value={r.category} onChange={e => update(i, "category", e.target.value)}
                    className="bg-slate-800 border border-slate-700 text-white text-xs rounded px-2 py-1 w-full" data-testid={`rule-cat-${i}`}>
                    {cats.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </td>
                <td className="px-2 py-1.5">
                  <input value={r.contains || ""} onChange={e => update(i, "contains", e.target.value)}
                    placeholder="(herhangi)" className="bg-slate-800 border border-slate-700 text-white text-xs rounded px-2 py-1 w-full" data-testid={`rule-contains-${i}`} />
                </td>
                <td className="px-2 py-1.5">
                  <input value={r.time || ""} onChange={e => update(i, "time", e.target.value)}
                    placeholder="(her saat)" className="bg-slate-800 border border-slate-700 text-white text-xs rounded px-2 py-1 w-full font-mono" data-testid={`rule-time-${i}`} />
                </td>
                <td className="px-2 py-1.5">
                  <select value={r.target} onChange={e => update(i, "target", e.target.value)}
                    className="bg-slate-800 border border-slate-700 text-white text-xs rounded px-2 py-1 w-full" data-testid={`rule-target-${i}`}>
                    {boats.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </td>
                <td className="px-2 py-1.5">
                  <div className="flex items-center gap-1">
                    <button onClick={() => move(i, -1)} className="p-1 text-slate-400 hover:text-white" title="Yukarı"><ArrowUp className="w-3.5 h-3.5" /></button>
                    <button onClick={() => move(i, 1)} className="p-1 text-slate-400 hover:text-white" title="Aşağı"><ArrowDown className="w-3.5 h-3.5" /></button>
                    <button onClick={() => removeRule(i)} className="p-1 text-red-400 hover:text-red-300" title="Sil" data-testid={`rule-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {rules.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500 text-xs">Kural yok</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <button onClick={addRule} className="mt-3 flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-xs font-medium rounded-lg" data-testid="rule-add-btn">
        <Plus className="w-3.5 h-3.5" /> Kural Ekle
      </button>

      {/* Kategori Aktarimi (saate gore) */}
      <div className="mt-8">
        <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-1">Kategori Aktarımı (Saate Göre)</h2>
        <p className="text-xs text-slate-500 mb-2">Belirli saatlerde bir ürünün gösterileceği listeyi değiştir. Örn: <b>sunset ≥ 20:30 → dinner</b> (geç sunset müşterisi akşam turunda, Dinner listesinde görünür).</p>
        <div className="rounded-lg border border-slate-700 overflow-hidden" data-testid="overrides-table">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-800 text-[11px] uppercase text-slate-400">
              <tr><th className="px-2 py-2">Kaynak Kategori</th><th className="px-2 py-2 w-32">Saat</th><th className="px-2 py-2">Hedef Liste</th><th className="px-2 py-2 w-12"></th></tr>
            </thead>
            <tbody>
              {overrides.map((o, i) => (
                <tr key={i} className="border-t border-slate-800" data-testid={`override-row-${i}`}>
                  <td className="px-2 py-1.5">
                    <select value={o.from} onChange={e => updateOv(i, "from", e.target.value)} className="bg-slate-800 border border-slate-700 text-white text-xs rounded px-2 py-1 w-full" data-testid={`ov-from-${i}`}>
                      {cats.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1.5"><input value={o.time || ""} onChange={e => updateOv(i, "time", e.target.value)} placeholder=">=20:30" className="bg-slate-800 border border-slate-700 text-white text-xs rounded px-2 py-1 w-full font-mono" data-testid={`ov-time-${i}`} /></td>
                  <td className="px-2 py-1.5">
                    <select value={o.to} onChange={e => updateOv(i, "to", e.target.value)} className="bg-slate-800 border border-slate-700 text-white text-xs rounded px-2 py-1 w-full" data-testid={`ov-to-${i}`}>
                      {cats.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1.5"><button onClick={() => removeOv(i)} className="p-1 text-red-400 hover:text-red-300" title="Sil"><Trash2 className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}
              {overrides.length === 0 && <tr><td colSpan={4} className="px-3 py-4 text-center text-slate-500 text-xs">Aktarım kuralı yok</td></tr>}
            </tbody>
          </table>
        </div>
        <button onClick={addOv} className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-xs font-medium rounded-lg" data-testid="ov-add-btn">
          <Plus className="w-3.5 h-3.5" /> Aktarım Ekle
        </button>
      </div>

      {/* Tekne -> Kategori esleme (bilgi) */}
      <div className="mt-8">
        <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-2">Tekne → Kategori Eşlemesi</h2>
        <p className="text-xs text-slate-500 mb-2">Her tekne bölmesi hangi kategoride görünür. (Eşlemesi olmayan tekne her kategoride görünür.)</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(boatCategory).map(([bname, slug]) => (
            <span key={bname} className="text-xs px-2.5 py-1 rounded-full bg-slate-800 border border-slate-700 text-slate-300">
              {bname} <span className="text-slate-500">→</span> <span className="text-emerald-400">{catName(slug)}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
